<template>
  <div>
    <section
      class="bp-wrap bp-hero"
      data-bp-station="hero"
      aria-labelledby="hero-title"
    >
      <div class="bp-hero__intro">
        <h1
          id="hero-title"
          class="bp-display"
        >
          Daniel Hagen
        </h1>
        <p class="bp-lead">
          Principal DevOps Engineer at Liatrio. I help companies transform their software delivery practices.
        </p>
        <a
          href="#contact"
          class="bp-btn bp-btn--signal bp-hero__mobile-cta"
        >
          Write to Daniel
          <BlueprintArrow direction="down" />
        </a>
        <BlueprintSchematic />
      </div>
      <ContactForm id="contact" />
    </section>

    <section
      id="about"
      class="bp-wrap bp-section"
      data-bp-station="notes"
      aria-labelledby="about-title"
    >
      <BlueprintMarks coord="H-01" />
      <div class="bp-section__head">
        <h2
          id="about-title"
          class="bp-h2"
        >
          <span class="bp-h2__ref">01</span>
          <span>Notes</span>
        </h2>
        <p class="bp-body">
          General notes on the drawing, in Daniel's own words. The portrait is Fig. 2.
        </p>
      </div>
      <div
        class="bp-notesheet"
        data-bp-draw
      >
        <figure class="bp-figure">
          <span
            class="bp-crop bp-crop--tl"
            aria-hidden="true"
          />
          <span
            class="bp-crop bp-crop--tr"
            aria-hidden="true"
          />
          <span
            class="bp-crop bp-crop--bl"
            aria-hidden="true"
          />
          <span
            class="bp-crop bp-crop--br"
            aria-hidden="true"
          />
          <div class="bp-figure__frame">
            <span class="bp-figure__plate">
              <NuxtImg
                src="/img/blog-author.jpg"
                format="webp"
                width="425"
                height="425"
                alt="Portrait of Daniel Hagen"
              />
            </span>
          </div>
          <figcaption>
            <span class="bp-label">Fig. 2 &nbsp;Daniel Hagen</span>
            <span class="bp-label">Photo © George Brainard</span>
          </figcaption>
        </figure>
        <div class="bp-notesheet__text">
          <h3 class="bp-notesheet__title">
            {{ home?.title || "I'm Daniel Hagen, a Technologist, Creator & Speaker" }}
          </h3>
          <ContentRenderer
            v-if="home"
            :value="home"
            class="bp-notes"
          />
        </div>
      </div>
    </section>

    <section
      id="record"
      class="bp-wrap bp-section"
      data-bp-station="record"
      aria-labelledby="record-title"
    >
      <BlueprintMarks coord="H-02" />
      <div class="bp-section__head">
        <h2
          id="record-title"
          class="bp-h2"
        >
          <span class="bp-h2__ref">02</span>
          <span>Career record</span>
        </h2>
        <p class="bp-body">
          Items 1 to 3 of Fig. 1, specified. From hands-on IT, through running IT for a game publisher, to consulting on how enterprises ship software.
        </p>
      </div>

      <div
        class="bp-specs"
        data-bp-draw
        data-bp-hover
      >
        <article
          class="bp-spec"
          aria-labelledby="spec-liatrio"
        >
          <svg
            class="bp-spec__draft"
            aria-hidden="true"
            focusable="false"
          >
            <rect
              x="0.5"
              y="0.5"
              width="100%"
              height="100%"
              pathLength="1"
            />
          </svg>
          <div class="bp-spec__strip">
            <span><span class="bp-balloon">3</span><span class="bp-label">Item 3</span></span>
            <span class="bp-label bp-node__state--live">Current</span>
          </div>
          <div class="bp-spec__title">
            <h3
              id="spec-liatrio"
              class="bp-spec__org"
            >
              Liatrio
            </h3>
            <p class="bp-spec__role">
              Principal DevOps Engineer
            </p>
          </div>
          <dl>
            <div class="bp-spec__row">
              <dt class="bp-label">
                Type
              </dt>
              <dd>Enterprise consulting firm</dd>
            </div>
            <div class="bp-spec__row">
              <dt class="bp-label">
                Function
              </dt>
              <dd>Helps companies transform their software delivery practices</dd>
            </div>
            <div class="bp-spec__row">
              <dt class="bp-label">
                Domain
              </dt>
              <dd>Cloud-native technologies, DevOps, automation</dd>
            </div>
            <div class="bp-spec__row">
              <dt class="bp-label">
                Stance
              </dt>
              <dd>Open Source to improve the quality of software delivery and the lives of those who deliver it</dd>
            </div>
          </dl>
        </article>

        <article
          class="bp-spec"
          aria-labelledby="spec-aspyr"
        >
          <svg
            class="bp-spec__draft"
            aria-hidden="true"
            focusable="false"
          >
            <rect
              x="0.5"
              y="0.5"
              width="100%"
              height="100%"
              pathLength="1"
            />
          </svg>
          <div class="bp-spec__strip">
            <span><span class="bp-balloon">2</span><span class="bp-label">Item 2</span></span>
            <span class="bp-label">Previous</span>
          </div>
          <div class="bp-spec__title">
            <h3
              id="spec-aspyr"
              class="bp-spec__org"
            >
              Aspyr Media
            </h3>
            <p class="bp-spec__role">
              Director of IT
            </p>
          </div>
          <dl>
            <div class="bp-spec__row">
              <dt class="bp-label">
                Type
              </dt>
              <dd>Austin-based video game publishing and development partner</dd>
            </div>
            <div class="bp-spec__row">
              <dt class="bp-label">
                Scope
              </dt>
              <dd>Internal and external IT services</dd>
            </div>
            <div class="bp-spec__row">
              <dt class="bp-label">
                Focus
              </dt>
              <dd>Technology for global customer-service initiatives and for employees' day-to-day work</dd>
            </div>
          </dl>
        </article>

        <article
          class="bp-spec bp-spec--narrow"
          aria-labelledby="spec-earlier"
        >
          <svg
            class="bp-spec__draft"
            aria-hidden="true"
            focusable="false"
          >
            <rect
              x="0.5"
              y="0.5"
              width="100%"
              height="100%"
              pathLength="1"
            />
          </svg>
          <div class="bp-spec__strip">
            <span><span class="bp-balloon">1</span><span class="bp-label">Item 1</span></span>
            <span class="bp-label">Earlier</span>
          </div>
          <div class="bp-spec__title">
            <h3
              id="spec-earlier"
              class="bp-spec__org"
            >
              Logitech and LifeSize Communi&shy;cations
            </h3>
            <p class="bp-spec__role">
              IT positions
            </p>
          </div>
          <dl>
            <div class="bp-spec__row">
              <dt class="bp-label">
                Sequence
              </dt>
              <dd>Prior to Aspyr Media</dd>
            </div>
          </dl>
        </article>
      </div>
    </section>

    <section
      id="practice"
      class="bp-wrap bp-section"
      data-bp-station="practice"
      aria-labelledby="practice-title"
    >
      <BlueprintMarks coord="H-03" />
      <div class="bp-section__head">
        <h2
          id="practice-title"
          class="bp-h2"
        >
          <span class="bp-h2__ref">03</span>
          <span>Practice, bill of materials</span>
        </h2>
        <p class="bp-body">
          The work that runs alongside the day job, itemized. Items 5 to 7 appear on the parallel rail of Fig. 1.
        </p>
      </div>

      <BlueprintDims :items="dimensions" />
      <table
        class="bp-bom"
        data-bp-hover
      >
        <caption class="bp-label">
          Table 1. Practice components. Source: Daniel's own account.
        </caption>
        <thead>
          <tr>
            <th
              scope="col"
              class="bp-label bp-bom__item"
            >
              Item
            </th>
            <th
              scope="col"
              class="bp-label"
            >
              Component
            </th>
            <th
              scope="col"
              class="bp-label"
            >
              Specification
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><span class="bp-balloon">5</span></td>
            <th scope="row">
              Open source
            </th>
            <td>Contributes to over 20 Open Source libraries, toolsets and systems images.</td>
          </tr>
          <tr>
            <td><span class="bp-balloon">6</span></td>
            <th scope="row">
              Speaking
            </th>
            <td>
              <p class="sr-only">
                Frequent speaker at:
              </p>
              <ul class="bp-venues">
                <li
                  v-for="venue in venues"
                  :key="venue"
                >
                  {{ venue }}
                </li>
              </ul>
            </td>
          </tr>
          <tr>
            <td><span class="bp-balloon">7</span></td>
            <th scope="row">
              Community
            </th>
            <td>Highly dedicated to the Austin technical community through meet-ups and training junior- and mid-level talent.</td>
          </tr>
          <tr>
            <td><span class="bp-balloon">8</span></td>
            <th scope="row">
              Credentials
            </th>
            <td>Several industry and project management certifications.</td>
          </tr>
        </tbody>
      </table>
      <h3
        id="schedule-title"
        class="bp-schedule__title"
      >
        Where Daniel has spoken
      </h3>
      <p class="bp-body bp-schedule__lead">
        A frequent speaker at these events. Organizing one? Send the details through the contact form.
      </p>
      <ol
        class="bp-venue-list"
        data-bp-draw
        data-bp-hover
        aria-labelledby="schedule-title"
      >
        <li
          v-for="(venue, index) in venues"
          :key="venue"
        >
          <span
            class="bp-balloon"
            aria-hidden="true"
          >{{ index + 1 }}</span>
          <span>{{ venue }}</span>
        </li>
      </ol>
    </section>

    <section
      class="bp-wrap bp-section"
      data-bp-station="contact"
      aria-labelledby="close-title"
    >
      <BlueprintMarks coord="H-04" />
      <div class="bp-close">
        <h2
          id="close-title"
          class="bp-close__title"
        >
          The drawing has one open port. It's labelled with your team.
        </h2>
        <div>
          <p class="bp-body">
            Consulting on software delivery, a role, a talk, or just a hello. Send a message and it routes straight to Daniel. The notes above are the whole biography.
          </p>
          <div class="bp-close__actions">
            <a
              href="#contact"
              class="bp-btn bp-btn--signal"
            >
              Write to Daniel
              <BlueprintArrow direction="up" />
            </a>
            <a
              href="#about"
              class="bp-btn"
            >
              Read the notes
              <BlueprintArrow direction="up" />
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
const { data: home } = await useAsyncData('home', () => {
  return queryCollection('content').path('/home').first()
})

const dimensions = [
  { value: 4, label: 'Organizations on the signal path', spoken: '4' },
  { value: 20, suffix: '+', label: 'Open Source libraries, toolsets and system images', spoken: 'Over 20' },
  { value: 6, label: 'Events spoken at, listed below', spoken: '6' }
]

const venues = [
  'AWS Summits',
  'ServerlessConf',
  'Open Cloud Foundation',
  'ChefConf',
  'AltConf',
  'DevOpsDays'
]
</script>
